import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Check } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) return toast.error("Les mots de passe ne correspondent pas");
    if (password.length < 8) return toast.error("Le mot de passe doit contenir au moins 8 caractères");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password, password_confirm: passwordConfirm });
      setDone(true);
      toast.success("Mot de passe modifié !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <h1 className="font-serif-display text-3xl mb-4">Lien invalide</h1>
        <p className="text-[#6B655C] mb-6">Ce lien de réinitialisation est incomplet ou invalide.</p>
        <Link to="/forgot-password" className="text-terracotta hover:underline">Faire une nouvelle demande</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="reset-success">
        <div className="w-20 h-20 mx-auto rounded-full bg-patchwork flex items-center justify-center mb-6">
          <Check size={36} className="text-white" strokeWidth={2.5} />
        </div>
        <h1 className="font-serif-display text-3xl mb-4">Mot de passe modifié</h1>
        <p className="text-[#6B655C] mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
        <button onClick={() => navigate("/login")} className="btn-primary">Se connecter</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2 text-center">Nouveau départ</p>
      <h1 className="font-serif-display text-4xl mb-10 text-center">Nouveau mot de passe</h1>
      <form onSubmit={submit} className="bg-white/70 backdrop-blur-md rounded-3xl border border-[#E5E1D8] p-8 space-y-4">
        <div>
          <label htmlFor="reset-pw" className="block text-sm text-[#6B655C] mb-1.5">Nouveau mot de passe</label>
          <div className="relative">
            <input id="reset-pw" required type={showPw ? "text" : "password"} minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8 caractères minimum" className="w-full p-3 pr-11 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="reset-password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B655C] hover:text-terracotta">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="reset-pw2" className="block text-sm text-[#6B655C] mb-1.5">Confirmer</label>
          <input id="reset-pw2" required type={showPw ? "text" : "password"} minLength={8} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="Retapez le mot de passe" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="reset-password-confirm-input" />
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="reset-submit-btn">
          {loading ? "Modification..." : "Modifier mon mot de passe"}
        </button>
      </form>
    </div>
  );
}
